/**
 * Compact commission-target strip shown inside the Invoiced tile of the
 * University Partnerships "Targets & performance" card (owner 2026-10-25).
 *
 * Shows percentages, never rates, pool or scheme name. Rows: personal;
 * regional (when the scheme has bands); contribution (my invoiced in the
 * region ÷ regional target). The whole strip is one button that opens
 * `CommissionProgressModal`. Focus ring comes from the global `:focus-visible`
 * rule in `index.css`.
 *
 * Owner 2026-10-27: one amount is shown, the live "Commission earned" payout
 * (`earned.payout`, scheme currency) when the response carries `earned`. A
 * missed gate adds nothing here; the modal explains it. A remainder-target
 * user (no assignment) sees only the personal row, labelled by
 * `personalTargetLabel`; a null derived target renders `—`.
 *
 * Owner 2026-10-27: the payout is compact K/M (`CompactMoney`), exact on hover.
 *
 * Owner 2026-10-28: one "New business" line (`newBusiness.newBusiness`, scheme
 * currency) when the response carries the block and the figure is positive.
 * `undefined` (older API), `null` or a zero figure adds nothing.
 */
import { ProgressMeter } from './ProgressMeter';
import { CompactMoney } from './TargetTiles';
import {
  PROGRESS_LABELS,
  EARNED_HEADING,
  NEW_BUSINESS_LABELS,
  contributionBar,
  hasEarned,
  hasNewBusiness,
  isRemainder,
  personalTargetLabel,
  progressBar,
} from '@/lib/commission-progress';
import { compareDecimalStrings } from '@/lib/company-documents';
import type { CommissionProgress } from '@/lib/types';

/** The new business figure when present and above zero; otherwise null. */
function positiveNewBusiness(progress: CommissionProgress): string | null {
  if (!hasNewBusiness(progress)) return null;
  const amount = progress.newBusiness.newBusiness;
  return compareDecimalStrings(amount, '0') === 1 ? amount : null;
}

interface StripRow {
  readonly key: string;
  readonly label: string;
  readonly pct: number | null;
}

function rowsOf(progress: CommissionProgress): StripRow[] {
  const remainder = isRemainder(progress);
  const rows: StripRow[] = [
    {
      key: 'personal',
      label: personalTargetLabel(progress),
      pct: progressBar(progress.personal.actual, progress.personal.target).pct,
    },
  ];
  // Remainder users (Paras) show their combined regional figure too (owner 2026-10-27).
  if (progress.regional) {
    rows.push({
      key: 'regional',
      label: PROGRESS_LABELS.regional,
      pct: progressBar(progress.regional.actual, progress.regional.target).pct,
    });
  }
  if (remainder) return rows;
  const contribution = contributionBar(progress);
  if (contribution) {
    rows.push({
      key: 'contribution',
      label: PROGRESS_LABELS.contribution,
      pct: contribution.pct,
    });
  }
  return rows;
}

export function CommissionProgressStrip({
  progress,
  onOpen,
}: {
  progress: CommissionProgress;
  onOpen: () => void;
}) {
  const rows = rowsOf(progress);
  const earned = hasEarned(progress) ? progress.earned : null;
  const newBusiness = positiveNewBusiness(progress);
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label="Commission targets, open details"
      className="mt-3 flex w-full min-w-0 cursor-pointer flex-col gap-2 rounded-xs border-t border-border bg-transparent pt-3 text-left hover:bg-surface-hover active:bg-surface-active"
    >
      {rows.map((row) => (
        <span key={row.key} className="flex min-w-0 flex-col gap-1">
          <span className="flex min-w-0 items-baseline justify-between gap-2 text-xs">
            <span className="min-w-0 truncate text-fg-muted" title={row.label}>
              {row.label}
            </span>
            <span className="tabular shrink-0 font-medium">
              {row.pct === null ? '—' : `${row.pct}%`}
            </span>
          </span>
          <ProgressMeter pct={row.pct} size="sm" label={row.label} />
        </span>
      ))}
      {earned ? (
        <span className="flex min-w-0 items-baseline justify-between gap-2 text-xs">
          <span className="min-w-0 truncate text-fg-muted" title={EARNED_HEADING}>
            {EARNED_HEADING}
          </span>
          <CompactMoney amount={earned.payout} currency={progress.currency} className="shrink-0 font-medium" />
        </span>
      ) : null}
      {newBusiness !== null ? (
        <span className="flex min-w-0 items-baseline justify-between gap-2 text-xs">
          <span className="min-w-0 truncate text-fg-muted" title={NEW_BUSINESS_LABELS.newBusiness}>
            {NEW_BUSINESS_LABELS.newBusiness}
          </span>
          <CompactMoney amount={newBusiness} currency={progress.currency} className="shrink-0 font-medium" />
        </span>
      ) : null}
    </button>
  );
}
